import { theme as defaultTheme, Icon } from "@mindlercare/ui-web";
import { useCallback, useRef, useState } from "react";
import { styled } from "styled-components";

const VIDEO_EXTENSIONS = [".mp4", ".mov", ".avi", ".webm", ".3gp", ".mkv"];

export const isVideoAsset = (asset: {
  url?: string;
  contentType?: string;
}): boolean => {
  const url = asset.url?.toLowerCase() ?? "";
  return (
    (asset.contentType?.startsWith("video/") ?? false) ||
    VIDEO_EXTENSIONS.some((ext) => url.endsWith(ext))
  );
};

interface IcbtVideoPlayerProps {
  url: string;
}

export const IcbtVideoPlayer = ({ url }: IcbtVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setIsAtStart(false);
    video.play();
  }, []);

  const handleEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    setIsAtStart(true);
  }, []);

  return (
    <Container>
      <VideoWrapper>
        <StyledVideo
          ref={videoRef}
          src={url}
          controls={!isAtStart}
          preload="metadata"
          onEnded={handleEnded}
        />
        {isAtStart && (
          <Overlay onClick={handlePlay}>
            <GradientOverlay />
            <PlayIconWrapper>
              <LargePlayIcon>
                <Icon.Play />
              </LargePlayIcon>
            </PlayIconWrapper>
          </Overlay>
        )}
      </VideoWrapper>
    </Container>
  );
};

const Container = styled.div`
  border-radius: ${defaultTheme.BORDER_RADIUS.LG};
  overflow: hidden;
  background-color: ${defaultTheme.COLORS.BLACK};
  margin: ${defaultTheme.SPACING.SM} 0;
`;

const VideoWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  position: relative;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const AbsoluteFill = styled.div`
  position: absolute;
  inset: 0;
`;

const Overlay = styled(AbsoluteFill)`
  cursor: pointer;
`;

const GradientOverlay = styled(AbsoluteFill)`
  background: linear-gradient(
    to right,
    ${defaultTheme.COLORS.PRIMARY},
    ${defaultTheme.COLORS.BRAND_ORANGE}
  );
  opacity: 0.85;
`;

const PlayIconWrapper = styled(AbsoluteFill)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LargePlayIcon = styled.div`
  color: white;
  width: 64px;
  height: 64px;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;
